/**
 * Workflow form helpers — turn a state's `form_fields[]` + the user's current
 * `data` into a plain list of `{ field, visible, required, enabled, error }`
 * descriptors that a renderer can map to inputs.
 *
 * Mirrors the backend's `fn_validate_case_data` rules so the user gets feedback
 * before the round-trip.
 */
import { isSatisfied, type JsonLogic } from "./jsonlogic";
import { toText } from "./toText";
import { typedGenericRfcError } from "../components/client/rfc.helpers";
import { t } from "../i18n/language";

export type ControlType =
  | "text" | "textarea" | "number" | "currency" | "date" | "datetime"
  | "select" | "multiselect" | "radio" | "checkbox" | "file" | "phone"
  | "email" | "rfc" | "curp" | "address" | "signature";

export interface CatalogOption {
  code: string;
  label: string;
  metadata?: Record<string, unknown> | null;
}

export interface FormFieldDef {
  field_key: string;
  label: string;
  control_type: ControlType;
  section: string | null;
  is_required: boolean;
  validation_regex: string | null;
  validation_message: string | null;
  min_value: number | null;
  max_value: number | null;
  min_length: number | null;
  max_length: number | null;
  visible_when: JsonLogic | null;
  required_when: JsonLogic | null;
  enabled_when: JsonLogic | null;
  options_source: "inline" | "catalog";
  options: CatalogOption[] | Array<{ label: string; value: unknown }> | null;
  /** Catalog slug for cascading selects: when `options_filter_field`
   *  is set the renderer must lazy-fetch via the catalog items endpoint
   *  using the current value of the filter field as `parent_code`. */
  options_catalog_slug: string | null;
  options_filter_field: string | null;
  default_value: string | null;
  placeholder: string | null;
  tooltip: string | null;
  is_pii: boolean;
  is_sensitive: boolean;
  display_order: number;
}

export interface FieldState {
  field: FormFieldDef;
  visible: boolean;
  required: boolean;
  enabled: boolean;
  value: unknown;
  error: string | null;
}

/** The stored code of an option, regardless of its shape: catalog
 *  options key on `code`, inline options on `value`. */
function optionCode(option: CatalogOption | { label: string; value: unknown }): unknown {
  return 'code' in option ? option.code : option.value;
}

/** The catalog of nationalities. "Spanish" and "Other" mean more than one thing in English, so a
 *  nationality is looked up under its own meaning (es.json: "Spanish ⟨nationality⟩"). */
export const NATIONALITY_CATALOG = "nationalities";

/** An option's label as the person at this screen reads it. */
export function optionLabel(field: FormFieldDef | undefined, label: string): string {
  return field?.options_catalog_slug === NATIONALITY_CATALOG ? t(label, "nationality") : label;
}

/**
 * Human-readable rendering of a captured `case_data` value.
 *
 * Select / radio / multiselect fields persist the option *code*
 * (`CA`, `H`, `MXN`); staff and clients should read the *label*
 * (`Canada`, `Male`, `MXN — Mexican peso`). The full option list is
 * inlined in the case detail payload, so the lookup is synchronous.
 *
 * Falls back to the raw string when the field has no options, or the
 * value isn't a known option — e.g. cascading selects (state by
 * country) whose options are loaded per-parent and aren't inlined.
 * File-pointer values are handled by the caller, not here.
 */
export function formatFieldValue(value: unknown, field?: FormFieldDef): string {
  if (value === null || value === undefined || value === '') return '—';

  const options = field?.options;
  if (Array.isArray(options) && options.length > 0) {
    const labelFor = (code: unknown): string => {
      const match = options.find((o) => optionCode(o) === code);
      if (match) return optionLabel(field, match.label);
      return typeof code === 'object' && code !== null ? JSON.stringify(code) : String(code);
    };
    if (Array.isArray(value)) {
      return value.length === 0 ? '—' : value.map(labelFor).join(', ');
    }
    return labelFor(value);
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return toText(value);
}

/** Compute the rendered state for every field in a state, given the current data. */
export function resolveFields(
  fields: FormFieldDef[],
  data: Record<string, unknown>,
): FieldState[] {
  return fields
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
    .map((field) => {
      const visible = isSatisfied(field.visible_when, data);
      const enabled = isSatisfied(field.enabled_when, data);
      const required =
        field.is_required || (field.required_when ? isSatisfied(field.required_when, data) : false);
      const value = data[field.field_key];
      const error = visible && enabled ? validateValue(field, value, required, data) : null;
      return { field, visible, required, enabled, value, error };
    });
}

// Control types that receive text-shaped validation (regex + length).
// `phone` is here AND gets the dedicated phone check below.
const _TEXT_LIKE_TYPES = new Set([
  'text', 'textarea', 'email', 'rfc', 'curp', 'phone', 'select', 'radio',
]);
const _NUMERIC_TYPES = new Set(['number', 'currency']);

function validateTextLike(field: FormFieldDef, text: string): string | null {
  if (field.validation_regex) {
    try {
      const re = new RegExp(field.validation_regex);
      if (!re.test(text)) return field.validation_message || `${field.label} has an invalid format`;
    } catch {
      // Invalid regex from server — ignore on client; backend will catch.
    }
  }
  if (field.min_length !== null && text.length < field.min_length) {
    return `${field.label} must be at least ${field.min_length} characters`;
  }
  if (field.max_length !== null && text.length > field.max_length) {
    return `${field.label} cannot exceed ${field.max_length} characters`;
  }
  return null;
}

function validateNumeric(field: FormFieldDef, value: unknown): string | null {
  const num = Number(value);
  if (Number.isNaN(num)) return `${field.label} must be numeric`;
  if (field.min_value !== null && num < field.min_value) {
    return `${field.label} must be ≥ ${field.min_value}`;
  }
  if (field.max_value !== null && num > field.max_value) {
    return `${field.label} must be ≤ ${field.max_value}`;
  }
  return null;
}

/** Mirror of fn_validate_case_data — returns null if value is acceptable.
 *  The optional `data` parameter carries the rest of the form so
 *  cross-field validations (e.g. phone format depending on
 *  country_residence) can run. Pass it from `resolveFields`. */
export function validateValue(
  field: FormFieldDef,
  value: unknown,
  required: boolean,
  data: Record<string, unknown> = {},
): string | null {
  if (value === null || value === undefined || value === '') {
    return required ? `${field.label} is required` : null;
  }
  if (_TEXT_LIKE_TYPES.has(field.control_type)) {
    const err = validateTextLike(field, toText(value));
    if (err) return err;
  }
  if (_NUMERIC_TYPES.has(field.control_type)) {
    const err = validateNumeric(field, value);
    if (err) return err;
  }
  // Universal age gate. Mirrors backend/app/core/compliance/age.py — both
  // layers must stay in sync (MINIMUM_INVESTING_AGE here, the backend
  // constant, and the schema seed tooltip).
  if (field.control_type === 'date' && field.field_key === 'birth_date') {
    return validateBirthDate(toText(value));
  }
  // A resident of Mexico cannot give a SAT generic key as their RFC (S-32; the server refuses
  // the submit with the same words).
  if (field.control_type === 'rfc') {
    const generic = typedGenericRfcError(getCountryResidence(data), toText(value));
    if (generic) return generic;
  }
  // Country-aware phone validation; E.164 normalization happens at submit
  // time in buildSubmitPayload.
  if (field.control_type === 'phone') {
    return validatePhone(toText(value), getCountryResidence(data));
  }
  return null;
}

function getCountryResidence(data: Record<string, unknown>): string | null {
  const v = data.country_residence;
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** Expected digit count of the national-format mobile number per
 *  country of residence. Numbers in E.164 (with leading +) are
 *  validated only for total digit length 7-15 — the country prefix
 *  is taken as authoritative when the user provides it. */
const PHONE_NATIONAL_LENGTH: Record<string, number> = {
  MX: 10,  // e.g. 5516552233
  US: 10,  // e.g. 4155551234
  CA: 10,  // same numbering plan as US
  ES: 9,   // e.g. 612345678
};

/** Country dialing codes for the supported residences. Used by
 *  buildSubmitPayload's normalizer to prepend when the user typed
 *  the national format. */
const PHONE_COUNTRY_CODE: Record<string, string> = {
  MX: '52',
  US: '1',
  CA: '1',
  ES: '34',
};

export function validatePhone(rawValue: string, countryResidence: string | null): string | null {
  const trimmed = rawValue.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  if (digits.length < 7) return 'Phone number is too short';
  if (digits.length > 15) return 'Phone number is too long';

  if (hasPlus) {
    // E.164 with user-provided country code — accept any 7-15 digit
    // total. Per-country prefix correctness is a server-side concern.
    return null;
  }

  // National format — check length against the country expectation.
  if (countryResidence !== null) {
    const expected = PHONE_NATIONAL_LENGTH[countryResidence];
    if (expected !== undefined && digits.length !== expected) {
      return `Phone must be ${expected} digits for ${countryResidence} (you entered ${digits.length})`;
    }
  }
  return null;
}

/** Normalize a phone value to E.164 (`+CC...` format) using
 *  `country_residence` as the country-code hint when the user typed
 *  the national format. Idempotent: a value already in E.164 is
 *  returned with separators stripped but the `+` preserved. */
export function normalizePhoneToE164(
  rawValue: string,
  countryResidence: string | null,
): string {
  const trimmed = rawValue.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (hasPlus) return `+${digits}`;
  const cc = countryResidence === null ? undefined : PHONE_COUNTRY_CODE[countryResidence];
  if (cc !== undefined) return `+${cc}${digits}`;
  // Unknown country — submit raw digits and let the backend decide.
  // (Better than dropping the value silently; worst case the regex
  // on the wire rejects it and the user gets a clear error.)
  return digits;
}

/** Reusable so DynamicField can also enforce `max` on the input. */
export const MINIMUM_INVESTING_AGE = 18;

/** Returns the latest birth date that still satisfies the minimum
 *  investing age, formatted as ISO YYYY-MM-DD for direct use as the
 *  `max` attribute on a `<input type="date">`. */
export function maxBirthDateISO(asOf: Date = new Date()): string {
  const cutoff = new Date(asOf);
  cutoff.setFullYear(cutoff.getFullYear() - MINIMUM_INVESTING_AGE);
  // Format as local YYYY-MM-DD so the picker treats it as a plain
  // calendar date, not a TZ-shifted instant.
  const yyyy = cutoff.getFullYear();
  const mm = String(cutoff.getMonth() + 1).padStart(2, '0');
  const dd = String(cutoff.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Returns null when the birth date satisfies the minimum age,
 *  otherwise a localised error message. */
export function validateBirthDate(iso: string, asOf: Date = new Date()): string | null {
  if (!iso) return null;
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return 'Invalid date';
  // Whole-years computation that respects "hasn't had birthday yet
  // this year" — mirrors the backend `age_in_years` helper.
  let age = asOf.getFullYear() - dob.getFullYear();
  const hadBirthdayThisYear =
    asOf.getMonth() > dob.getMonth() ||
    (asOf.getMonth() === dob.getMonth() && asOf.getDate() >= dob.getDate());
  if (!hadBirthdayThisYear) age -= 1;
  if (age < MINIMUM_INVESTING_AGE) {
    return `You must be at least ${MINIMUM_INVESTING_AGE} years old to open an account.`;
  }
  return null;
}

/** True if every visible+enabled field is valid (used to gate the "Submit" button). */
export function isFormValid(states: FieldState[]): boolean {
  return states.every((s) => !s.visible || !s.enabled || s.error === null);
}

/** Countries where income is typically NOT denominated in Mexican pesos.
 *  Keyed by the `country_residence` code captured earlier in onboarding. */
const NON_MXN_INCOME_COUNTRIES: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
};

/**
 * Country-aware nudge for the KYC `income_currency` field.
 *
 * We deliberately do NOT auto-pick a currency — the field stays required
 * and empty so the client makes a conscious choice, which matters because
 * the captured currency feeds the FX book (each product is funded in a
 * currency-matched account). But when the client's country of residence is
 * one where income is typically in USD, a silent MXN selection would be an
 * easy mistake, so we surface a reminder to confirm.
 *
 * Returns the hint message, or null when no nudge is warranted: MX (or any
 * unlisted) residence, a currency already set to USD, or no country yet.
 */
export function incomeCurrencyHint(
  countryResidence: unknown,
  selectedCurrency: unknown,
): string | null {
  const country = typeof countryResidence === 'string' ? countryResidence : '';
  const place = NON_MXN_INCOME_COUNTRIES[country];
  if (place === undefined) return null;
  // Once they've confirmed ANY non-peso currency there's nothing to nudge.
  // (Guards against a future option beyond USD, e.g. CAD, silently slipping
  // the nudge through.)
  if (
    typeof selectedCurrency === 'string'
    && selectedCurrency !== ''
    && selectedCurrency !== 'MXN'
  ) {
    return null;
  }
  return (
    `You told us you reside in ${place}. Most clients there are paid in a ` +
    'non-peso currency — please confirm the currency of your income above.'
  );
}

/** Filter the data payload sent to the backend to visible+enabled fields only.
 *  Also normalizes phone fields to E.164 using `country_residence`
 *  as the country-code hint so the wire/DB format stays canonical
 *  even when the user typed the national format. */
export function buildSubmitPayload(states: FieldState[]): Array<{ field_key: string; value: unknown }> {
  const countryResidence = (() => {
    const s = states.find((x) => x.field.field_key === 'country_residence');
    const v = s?.value;
    return typeof v === 'string' && v.length > 0 ? v : null;
  })();

  return states
    .filter((s) => s.visible && s.enabled)
    .map((s) => {
      if (s.field.control_type === 'phone' && typeof s.value === 'string') {
        const normalized = normalizePhoneToE164(s.value, countryResidence);
        return { field_key: s.field.field_key, value: normalized === '' ? null : normalized };
      }
      return { field_key: s.field.field_key, value: s.value ?? null };
    });
}

/** True when the client holds NO Mexican nationality (a dual national is read on both) and lives
 *  in Mexico: the person the form fills the portal's placeholder RFC in for. */
export function isForeignNationalInMexico(data: Record<string, unknown>): boolean {
  const first = data.nationality;
  const second = data.second_nationality;
  return (
    typeof first === 'string' &&
    first !== '' &&
    first !== 'MX' &&
    second !== 'MX' &&
    data.country_residence === 'MX'
  );
}
